import { cn } from '@lib/utils';
import React, { useEffect } from 'react';

export enum StepState {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  NAVIGABLE = 'NAVIGABLE',
}

export interface StepConfig {
  label: string;
  step: number;
  guidance?: string | JSX.Element;
}

export interface StepInfo extends StepConfig {
  state: StepState;
  isValid: boolean;
}

interface ModalStepsProps {
  stepsInfo: StepInfo[];
  onStepClick: (step: number) => void;
  className?: string;
}

export const ModalSteps: React.FC<ModalStepsProps> = (props) => {
  const { stepsInfo, onStepClick, className = '' } = props;
  return (
    <div className={cn('flex w-full items-center gap-1.5', className)}>
      {stepsInfo.map(({ step, label, state }) => (
        <div
          key={step}
          className={cn('flex-1 min-w-0', state === StepState.DISABLED ? 'cursor-not-allowed' : 'cursor-pointer')}
          onClick={() => {
            if (state !== StepState.DISABLED) {
              onStepClick(step);
            }
          }}
        >
          <div
            className={cn('h-1 w-full', {
              'bg-green-500': state === StepState.ACTIVE,
              'bg-green-300': state === StepState.NAVIGABLE,
              'bg-green-100':
                state === StepState.DISABLED || (state !== StepState.ACTIVE && state !== StepState.NAVIGABLE),
            })}
          />
          <div
            className={cn('mt-0.5 text-center font-light truncate', {
              'cursor-not-allowed': state === StepState.DISABLED,
              'cursor-pointer': state !== StepState.DISABLED,
              'text-green-700': state === StepState.ACTIVE,
              'text-green-500': state === StepState.NAVIGABLE,
              'text-gray-400':
                state === StepState.DISABLED || (state !== StepState.ACTIVE && state !== StepState.NAVIGABLE),
            })}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

export function useSteps(stepData: StepConfig[], initialStep: number, isEdit = false) {
  const [step, setStep] = React.useState(initialStep);

  const [stepsInfo, setStepsInfo] = React.useState<StepInfo[]>(() =>
    stepData.map((sConfig) => ({
      ...sConfig,
      state: sConfig.step === initialStep ? StepState.ACTIVE : isEdit ? StepState.NAVIGABLE : StepState.DISABLED,
      isValid: false,
    })),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: we don't want to re-run this effect when stepsInfo changes
  useEffect(() => {
    let newInitialSteps = stepData.map((sConfig) => ({
      ...sConfig,
      state: sConfig.step === initialStep ? StepState.ACTIVE : isEdit ? StepState.NAVIGABLE : StepState.DISABLED,
      isValid: stepsInfo.find((si) => si.step === sConfig.step)?.isValid || false,
    }));

    if (isEdit) {
      newInitialSteps = newInitialSteps.map((s) => {
        return {
          ...s,
          isValid: s.step < initialStep ? true : s.isValid,
        };
      });
    }
    setStepsInfo(newInitialSteps);
    setStep(initialStep);
  }, [isEdit, initialStep, stepData]);

  /**
   * This function is used to update the state of the steps.
   * It assumes that the currentStep is valid.
   *
   * @param currentStep - The current step that was just completed.
   */
  const updateStepState = React.useCallback(
    (currentStep: number) => {
      const validatedStepIndex = stepData.findIndex((s) => s.step === currentStep);
      if (validatedStepIndex === -1) {
        console.error(`Step ${currentStep} not found in stepData.`);
        return;
      }

      const nextStepInSequence =
        validatedStepIndex < stepData.length - 1 ? stepData[validatedStepIndex + 1] : undefined;

      setStepsInfo((prevSteps) =>
        prevSteps.map((sInfo) => {
          let newState = sInfo.state;
          let newIsValid = sInfo.isValid;

          if (sInfo.step === currentStep) {
            newIsValid = true;
            newState = StepState.NAVIGABLE;
          } else if (nextStepInSequence && sInfo.step === nextStepInSequence.step) {
            newState = StepState.ACTIVE;
          } else if (isEdit && sInfo.state !== StepState.DISABLED && newState !== StepState.ACTIVE) {
            newState = StepState.NAVIGABLE;
          }
          return { ...sInfo, state: newState, isValid: newIsValid };
        }),
      );

      if (nextStepInSequence) {
        setStep(nextStepInSequence.step);
      }
    },
    [stepData, isEdit],
  );

  const handleStepClick = React.useCallback(
    (targetClickedStep: number) => {
      const targetStepInfo = stepsInfo.find((s) => s.step === targetClickedStep);
      if (!targetStepInfo || targetStepInfo.state === StepState.DISABLED) {
        return;
      }

      const previouslyActiveStep = step;

      setStepsInfo((prevSteps) =>
        prevSteps.map((info) => {
          if (info.step === targetClickedStep) {
            return { ...info, state: StepState.ACTIVE };
          }
          if (info.step === previouslyActiveStep && info.step !== targetClickedStep) {
            return { ...info, state: StepState.NAVIGABLE };
          }
          if (isEdit && info.state !== StepState.DISABLED && info.step !== targetClickedStep) {
            return { ...info, state: StepState.NAVIGABLE };
          }
          return info;
        }),
      );
      setStep(targetClickedStep);
    },
    [stepsInfo, step, isEdit],
  );

  const resetSteps = React.useCallback(() => {
    const newInitialSteps = stepData.map((sConfig) => ({
      ...sConfig,
      state: sConfig.step === initialStep ? StepState.ACTIVE : isEdit ? StepState.NAVIGABLE : StepState.DISABLED,
      isValid: false,
    }));
    setStepsInfo(newInitialSteps);
    setStep(initialStep);
  }, [initialStep, isEdit, stepData]);

  return {
    step,
    setStep,
    stepsInfo,
    setStepsInfo,
    updateStepState,
    handleStepClick,
    resetSteps,
  };
}
